import type { Entity } from './api.js';

/**
 * **R7 — la recherche par le contenu** (`recherche-contenu.md` §4.7, RC7, RC8).
 *
 * Elle est **cliente, sur le miroir** (C6) : aucune route, aucun index serveur, et
 * donc sa limite — elle porte sur ce qui est synchronisé — est écrite à l'écran.
 * Ce qu'elle lit est ce que R5 a écrit : les **mots-clés** et la **description**
 * d'une photo, qui voyagent aussi sur les arêtes (D28), plus ce que l'objet disait
 * déjà de lui-même.
 *
 * Le module est **pur** — ni signal, ni DOM — : un moteur qui se trompe rend une
 * grille vide, et une grille vide se lit comme « je n'ai pas cette photo ». C'est
 * la leçon de R1 et de R3, et la raison pour laquelle chaque règle ci-dessous a sa
 * mesure dans `recherche.spec.ts`, sur des **fixtures synthétiques** — jamais les
 * descriptions des photos de la production, qui sont privées.
 *
 * ### Ce qu'elle ne sait pas faire, et c'est voulu
 *
 * Elle ne comprend pas les paraphrases : « bord de mer » ne trouve pas « plage ».
 * C'est la couche sémantique de RC8 qui le comblerait, et elle n'est ajoutée **que
 * si l'usage montre qu'il en faut une**.
 */

/** Ce que pèse un mot selon l'endroit où il est trouvé (§4.7). */
export const WEIGHT = {
  tags: 3,
  description: 2,
  albums: 2,
  date: 2,
  name: 1,
} as const;

type Field = keyof typeof WEIGHT;

/**
 * **Les mots qui ne cherchent rien.** « une plage au bord de la mer » : sans cette
 * liste, « une », « au », « de » et « la » se trouvent dans presque toutes les
 * descriptions, et le classement « par nombre de mots trouvés » mettrait en tête la
 * description la plus bavarde plutôt que la plage.
 *
 * Elle est courte et **fermée** : un mot de sens qui y entrerait deviendrait
 * introuvable, et c'est le pire défaut d'un moteur de recherche.
 */
const STOP = new Set([
  'a', 'au', 'aux', 'avec', 'ce', 'ces', 'dans', 'de', 'des', 'du', 'd', 'en', 'et', 'l', 'la',
  'le', 'les', 'leur', 'ou', 'par', 'pour', 'qu', 'que', 'qui', 'sa', 'se', 'ses', 'son', 'sur',
  'un', 'une', 'y',
]);

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/**
 * **RC7 — l'insensibilité se fait ici, à la recherche, jamais au stockage.** Les
 * mots-clés gardent leurs accents ; c'est la comparaison qui les ignore.
 *
 * Minuscules, diacritiques retirés, apostrophes et ponctuation comme séparateurs.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase();
}

/**
 * **Singulier et pluriel confondus** — `-s` et `-x`, rien de plus savant. « chevaux »
 * ne rejoint pas « cheval », et c'est accepté : un radical français juste est un
 * autre projet, et une règle fausse rapprocherait des mots qui n'ont rien à voir.
 * Les mots courts sont laissés tels quels — « os », « pas » ne sont pas des pluriels.
 */
export function stem(word: string): string {
  return word.length > 3 && /[sx]$/.test(word) ? word.slice(0, -1) : word;
}

export function tokens(text: string): string[] {
  return fold(text)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 0)
    .map(stem);
}

/** Les mots d'une requête : pliés, sans mots vides, sans doublon — dans l'ordre tapé. */
export function queryWords(query: string): string[] {
  const words = tokens(query).filter((w) => !STOP.has(w));
  return [...new Set(words)];
}

/** Ce qu'une entité donne à chercher, champ par champ, en mots pliés. */
export type Indexed = Readonly<Record<Field, readonly string[]>>;

/**
 * **La date en mots** (RC5) — « août 2026 », dérivée de `DocumentDate` côté client,
 * **jamais** demandée au modèle : une photo du 25 décembre n'est pas une photo de
 * Noël, et le modèle ne verra jamais la date.
 */
export function dateWords(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * `albumNames` ne sert qu'au **propriétaire** : `albums` n'existe que sur l'entité
 * (I-27), jamais sur une arête. Un objet reçu ne se trouve donc pas par le nom de la
 * collection où il est rangé chez l'autre — il n'y a rien à lire.
 */
export function indexOf(entity: Entity, albumNames: ReadonlyMap<string, string>): Indexed {
  return {
    tags: (entity.Tags ?? []).flatMap(tokens),
    description: tokens(entity.Description ?? ''),
    albums: (entity.albums ?? []).flatMap((aid) => tokens(albumNames.get(aid) ?? '')),
    date: tokens(dateWords(entity.DocumentDate ?? entity.CreatedAt)),
    name: tokens(entity.Name ?? ''),
  };
}

/**
 * Comment se lit le **dernier** mot tapé.
 *
 * - `exact` — la requête finit par un espace ou une ponctuation : le mot est fini ;
 * - `prefix` — on est en train de le taper : « chi » doit déjà trouver « chien » ;
 * - `extend` — on tape un mot qui est **aussi** un mot vide : « la » est peut-être
 *   le début de « lagon ». Il ne trouve alors que ce qui le **prolonge**, jamais
 *   « la » lui-même, sans quoi il ferait remonter toutes les descriptions.
 */
export type LastMode = 'exact' | 'prefix' | 'extend';

export interface Query {
  readonly words: readonly string[];
  readonly last: LastMode;
}

export function parseQuery(query: string): Query {
  const raw = tokens(query);
  const typing = /[\p{L}\p{N}]$/u.test(query);
  const words = queryWords(query);
  const tail = raw[raw.length - 1];
  if (!typing || tail === undefined) return { words, last: 'exact' };
  if (STOP.has(tail)) {
    if (words.includes(tail)) return { words, last: 'prefix' };
    return { words: [...words, tail], last: 'extend' };
  }
  // Le dernier mot tapé est bien le dernier de la liste : `queryWords` dédoublonne
  // en gardant la première occurrence, et « chien chat chien » le déplacerait.
  return words[words.length - 1] === tail ? { words, last: 'prefix' } : { words, last: 'exact' };
}

/**
 * **Le score d'une entité pour une requête** — `0` : elle n'en est pas.
 *
 * Trois règles de §4.7, et chacune vient d'un test :
 *
 * - **Plusieurs mots classent, ils n'excluent pas.** L'exigence stricte rendait
 *   « une plage au bord de la mer » vide. Le score met en tête ce qui trouve le plus
 *   de mots, puis ce qui les trouve aux meilleurs endroits : un mot-clé vaut plus
 *   qu'un mot de description, qui vaut plus qu'un nom de fichier.
 * - **Le dernier mot est un préfixe** tant qu'on le tape ; les autres sont entiers,
 *   sinon « la » trouverait « lapin ».
 * - **« Aucune photo » est une réponse.** Un score nul n'est pas repêché : c'est
 *   ce qui permet à « un chat » de rendre zéro là où l'embedding rendait cinq photos
 *   quelconques.
 */
export function score(index: Indexed, query: Query): number {
  let found = 0;
  let weight = 0;
  query.words.forEach((word, i) => {
    const mode: LastMode = i === query.words.length - 1 ? query.last : 'exact';
    const hits = (w: string): boolean =>
      mode === 'exact' ? w === word : mode === 'prefix' ? w.startsWith(word) : w.startsWith(word) && w !== word;
    let best = 0;
    for (const field of Object.keys(WEIGHT) as Field[]) {
      if (index[field].some(hits)) best = Math.max(best, WEIGHT[field]);
    }
    if (best > 0) {
      found += 1;
      weight += best;
    }
  });
  return found === 0 ? 0 : found * 100 + weight;
}

/**
 * **Chercher dans une liste** — les entités qui trouvent au moins un mot, les
 * meilleures d'abord ; à score égal, l'ordre reçu, c'est-à-dire le tri de l'écran.
 *
 * Une requête sans mot cherchable — vide, ou faite de seuls mots vides terminés —
 * **rend la liste telle quelle** : « la » n'est pas une recherche, et vider la
 * grille pour autant serait un mensonge.
 */
export function search<T extends Entity>(
  list: readonly T[],
  query: string,
  albumNames: ReadonlyMap<string, string> = new Map(),
): readonly T[] {
  const parsed = parseQuery(query);
  if (parsed.words.length === 0) return list;
  return list
    .map((entity, order) => ({ entity, order, s: score(indexOf(entity, albumNames), parsed) }))
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s || a.order - b.order)
    .map((r) => r.entity);
}

/** Vrai quand la requête porte au moins un mot cherchable — l'écran passe en mode résultats. */
export const isSearching = (query: string): boolean => parseQuery(query).words.length > 0;
